import { GuideSection } from '@/components/guides/GuideSection';
import { GuideSteps, GuideStep } from '@/components/guides/GuideSteps';
import { GuideCallout } from '@/components/guides/GuideCallout';
import { GuideAction } from '@/components/guides/GuideAction';

export function ScheduleAssessmentSection() {
  return (
    <GuideSection
      id="schedule-assessment"
      eyebrow="Online assessments"
      title="Schedule an online assessment"
      description="Same wizard as interviews, tuned for take-home tests and timed assessments."
    >
      <GuideSteps>
        <GuideStep number={1} title="Open the Tests tab">
          <p>
            Click <strong>Tests</strong> in the sidebar. This is your list of online assessment
            sessions — HackerRank-style coding tests, take-home problems, whiteboarding drills.
          </p>
          <GuideAction href="/tests">Open Tests</GuideAction>
        </GuideStep>

        <GuideStep number={2} title="Click “Schedule test”">
          <p>Top-right of the Tests page. The same 3-step wizard opens, in test mode.</p>
        </GuideStep>

        <GuideStep number={3} title="Step 1 — Details differ slightly">
          <p>For tests the wizard asks for:</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>Test name</strong> instead of position title (e.g., "HackerRank: DSA Round").
            </li>
            <li>
              <strong>Due date</strong> instead of interview time — when the candidate has to finish
              by.
            </li>
            <li>Same candidate picker, same notes field.</li>
          </ul>
        </GuideStep>

        <GuideStep number={4} title="Step 2 — Materials">
          <p>
            Upload the test brief (PDF or pasted text) and anything else the candidate needs:
            reference problems, starter code link, grading rubric. Same parse flow as interviews.
          </p>
        </GuideStep>

        <GuideStep number={5} title="Step 3 — Confirm and dispatch">
          <p>
            Click <strong>Schedule test</strong>. The session is created as type{' '}
            <code>online_assessment</code>, a license is issued or reused, and the candidate gets an
            email with their key plus instructions for the test.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideCallout variant="info" title="What's different from interviews">
        <ul className="ml-4 list-disc space-y-1">
          <li>
            <strong>Pricing</strong> — assessments bill at a lower per-session rate than interviews.
            See the Billing guide for exact numbers.
          </li>
          <li>
            <strong>Session length</strong> — typically longer (hours, not minutes), since
            candidates work through problems on their own schedule.
          </li>
          <li>
            <strong>Transcripts</strong> — still captured, but you'll see fewer back-and-forth turns
            than in a live interview. The coaching feed still appears in the detail dialog.
          </li>
        </ul>
      </GuideCallout>

      <GuideCallout variant="tip" title="When to use an assessment vs an interview">
        <p>
          Use <strong>interview mode</strong> for anything live with another human on the call
          (recruiter screens, technical rounds, behavioral rounds). Use <strong>test mode</strong>{' '}
          for solo work: take-home assignments, timed coding tests, written case studies.
        </p>
      </GuideCallout>
    </GuideSection>
  );
}
